import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Bot,
  Brain,
  CheckCircle2,
  Clock,
  Heart,
  Headphones,
  MessageCircle,
  Shield,
  Smile,
  Star,
  Users,
  Zap,
  AlertTriangle,
  DollarSign,
  Timer,
  RotateCcw,
  RefreshCw,
  Settings,
  UserCheck,
  Navigation,
  CreditCard,
  Wrench,
  ClipboardList,
  Phone,
  BookOpen,
  ShoppingCart,
  Database,
  Filter,
  Gauge,
  GraduationCap,
  Map,
  ShoppingBag,
  Globe,
  Bell,
  TrendingUp,
  CheckCheck,
  BookUser,
  Ticket,
  Puzzle,
  Moon,
  ArrowRightLeft,
  Hourglass,
  Network,
  Gift,
  PiggyBank,
  Package,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'features', label: 'Features' },
  { id: 'benefits', label: 'Benefits' },
  { id: 'challenge', label: 'Challenge' },
  { id: 'solution', label: 'Solution' },
  { id: 'impact', label: 'Impact' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'roi', label: 'ROI' },
  { id: 'roadmap', label: 'Roadmap' },
];

const AICustomerCarePage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const capabilities = [
    '24/7 AI-powered customer support',
    'Natural language understanding across channels',
    'Personalized customer interactions',
    'Seamless human handoff when needed',
    'Multi-language support',
    'Continuous learning and improvement',
  ];

  const features = [
    {
      icon: Bot,
      title: 'Intelligent Automation',
      desc: 'AI-driven responses and issue resolution',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: MessageCircle,
      title: 'Omnichannel Support',
      desc: 'Unified experience across all channels',
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      icon: Brain,
      title: 'Smart Context',
      desc: 'Context-aware conversations and memory',
      gradient: 'from-rose-500 to-red-500',
    },
    {
      icon: Heart,
      title: 'Empathetic Care',
      desc: 'Sentiment-aware responses and escalation',
      gradient: 'from-red-500 to-orange-500',
    },
  ];

  const benefits = [
    { icon: Clock, text: 'Instant response times 24/7' },
    { icon: Smile, text: 'Enhanced customer satisfaction' },
    { icon: Zap, text: 'Reduced operational costs' },
    { icon: Users, text: 'Scalable customer support' },
    { icon: Star, text: 'Improved first-contact resolution' },
    { icon: Shield, text: 'Consistent service quality' },
  ];

  const painPoints = [
    { icon: Clock, title: 'Long wait times and peak-hour backlogs', desc: 'Customer frustration increases with delays' },
    { icon: DollarSign, title: 'High cost per interaction and staffing pressure', desc: 'Scaling requires expensive headcount growth' },
    { icon: ArrowRightLeft, title: 'Inconsistent responses across channels', desc: 'Quality varies by agent and channel' },
    { icon: Hourglass, title: 'Slow resolution due to manual lookups and repetitive work', desc: 'Agents waste time on routine tasks' },
    { icon: Moon, title: 'Limited after-hours coverage and language constraints', desc: 'Global customers face accessibility barriers' },
  ];

  const solutionCards = [
    { icon: MessageCircle, title: 'Natural Language Understanding', desc: 'Intent classification and context awareness' },
    { icon: Navigation, title: 'Guided Conversations', desc: 'Capture the right information every time' },
    { icon: Settings, title: 'Automated Workflows', desc: 'Execute actions in enterprise systems' },
    { icon: RefreshCw, title: 'Case Lifecycle Automation', desc: 'Create/update/route/close automatically' },
    { icon: UserCheck, title: 'Human-in-the-Loop', desc: 'Seamless escalation with full context' },
  ];

  const impactMetrics = [
    { icon: PiggyBank, value: '40–70%', label: 'Lower Cost per Interaction', tag: 'Deflection & Automation' },
    { icon: Zap, value: 'Up to 3×', label: 'Faster Response Times', tag: 'Across All Channels' },
    { icon: Smile, value: '+10–20 pts', label: 'Higher Customer Satisfaction', tag: 'CSAT Improvement' },
    { icon: Users, value: '30–50%', label: 'Reduced Agent Workload', tag: 'Focus on Complex Cases' },
    { icon: Clock, value: '24/7', label: 'Always-On Coverage', tag: 'Without 24/7 Staffing' },
  ];

  const qualityMetrics = [
    { icon: CheckCheck, value: '+15–25 pts', label: 'First Contact Resolution (FCR)', tag: '↑ Quality Lift', desc: 'Structured conversations capture complete info upfront' },
    { icon: Timer, value: '−30–60%', label: 'Average Handle Time (AHT)', tag: '↓ Efficiency Gain', desc: 'Automated data collection and faster backend lookups' },
    { icon: RotateCcw, value: '−40–70%', label: 'Reopen / Repeat Contacts', tag: '↓ Better Outcomes', desc: 'Consistent, accurate answers prevent follow-up queries' },
    { icon: Navigation, value: '92%', label: 'Routing Accuracy', tag: '↑ Precision', desc: 'Smart intent classification + customer context awareness' },
  ];

  const deflectionCards = [
    { icon: Bot, title: 'Automated Containment', desc: 'Handle common inquiries instantly: order status, billing, KB lookups' },
    { icon: CheckCircle2, title: 'Consistent Answers', desc: 'Responses grounded in approved knowledge sources' },
    { icon: TrendingUp, title: 'Elastic Scale', desc: 'Peak demand handled without queueing or drop-offs' },
    { icon: Globe, title: 'Multilingual Support', desc: 'Optional: global audiences in 50+ languages' },
    { icon: Bell, title: 'Proactive Notifications', desc: 'Optional: email/SMS/chat updates and alerts' },
  ];

  const useCases = [
    {
      icon: ShoppingBag,
      title: 'ORDER & ACCOUNT',
      items: [
        'Order status, delivery updates, returns & exchanges',
        'Account changes, profile updates, preference settings',
        'Subscription management and auto-renewals',
      ],
    },
    {
      icon: CreditCard,
      title: 'BILLING & PAYMENTS',
      items: [
        'Invoice lookup and payment status verification',
        'Refund initiation and dispute intake process',
        'Plan changes and eligibility checks',
      ],
    },
    {
      icon: Wrench,
      title: 'SUPPORT & TROUBLESHOOT',
      items: [
        'Guided diagnostics and step-by-step help',
        'Knowledge base Q&A from approved docs',
        'Warranty claims and service requests',
      ],
    },
    {
      icon: ClipboardList,
      title: 'CASE MANAGEMENT',
      items: [
        'Create, update, and close tickets automatically',
        'Smart routing and priority classification',
        'Status updates and proactive customer notifications',
      ],
    },
  ];

  const integrations = [
    { icon: BookUser, title: 'CRM', items: 'Salesforce, Microsoft Dynamics, HubSpot, custom CRM' },
    { icon: Ticket, title: 'Support & Ticketing', items: 'Zendesk, Freshdesk, ServiceNow CSM, Jira Service Management' },
    { icon: Phone, title: 'Contact Center & Telephony', items: 'CCaaS/UCaaS platforms, SIP/IVR, voice providers' },
    { icon: BookOpen, title: 'Knowledge & Content', items: 'Confluence, SharePoint, internal KBs, websites/FAQs' },
    { icon: ShoppingCart, title: 'Commerce & Orders', items: 'Shopify, Magento, OMS/ERP systems' },
    { icon: DollarSign, title: 'Billing & Subscriptions', items: 'Stripe, Zuora, NetSuite, custom billing platforms' },
    { icon: MessageCircle, title: 'Messaging', items: 'Email, SMS, WhatsApp, in-app chat' },
    { icon: Database, title: 'Data & Apps', items: 'SQL, data warehouses, internal APIs, custom applications' },
  ];

  const roiMetrics = [
    { value: '9–14 mo', label: 'Typical Payback Period' },
    { value: '~280%', label: '3-Year ROI (Average)' },
    { value: '60%', label: 'Automated Handling' },
  ];

  const roiDrivers = [
    { icon: Filter, title: 'Containment/deflection of routine inquiries', desc: 'Resolve 40–60%+ of requests without agent intervention' },
    { icon: Gauge, title: 'Reduced average handle time for assisted agents', desc: '30–60% efficiency gain through automation' },
    { icon: GraduationCap, title: 'Lower training and ramp time for new agents', desc: 'AI-guided workflows reduce onboarding complexity' },
    { icon: Navigation, title: 'Fewer transfers and reopens through smarter routing', desc: 'Right-first-time resolution improves outcomes' },
    { icon: Clock, title: '24/7 coverage without 24/7 staffing costs', desc: 'Always-on service without additional headcount' },
  ];

  const roadmapPhases = [
    {
      title: 'Phase 1: Quick Wins',
      duration: '4–8 weeks',
      items: [
        'Top intents (FAQs + case creation)',
        'CRM/ticketing integration',
        'Approved knowledge grounding and safe responses',
      ],
    },
    {
      title: 'Phase 2: Workflow Automation',
      duration: '6–10 weeks',
      items: [
        'Order/billing actions and status updates',
        'Routing and prioritization workflows',
        'Notifications and proactive messaging',
      ],
    },
    {
      title: 'Phase 3: Optimization',
      duration: 'Ongoing',
      items: [
        'Expand intents and channels',
        'Multilingual and personalization (optional)',
        'Analytics, QA loop, and continuous improvement',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="aiCustomerCare" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian AI Customer Care","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Deliver instant, accurate customer support with Digian's AI-powered care platform across voice, chat, and email.","url":"https://digian.ai/ai-customer-care","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Which channels does AI Customer Care support?","acceptedAnswer":{"@type":"Answer","text":"Voice, web chat, email, and SMS are supported out of the box. Social media channels (WhatsApp, Facebook Messenger) are available on Enterprise plans."}},{"@type":"Question","name":"How does human handoff work?","acceptedAnswer":{"@type":"Answer","text":"When the AI detects a complex issue or low confidence, it seamlessly transfers the conversation, with full context, to a live agent. Handoff triggers are configurable."}},{"@type":"Question","name":"What languages are supported?","acceptedAnswer":{"@type":"Answer","text":"The AI Customer Care agent supports multiple languages. Language detection is automatic. Contact us for the full list of supported languages."}},{"@type":"Question","name":"How long does implementation take?","acceptedAnswer":{"@type":"Answer","text":"Most customers are live within 2–4 weeks. Enterprise deployments with deep integrations may take 6–8 weeks. A dedicated onboarding engineer guides the process."}},{"@type":"Question","name":"Can I customize the AI persona and tone?","acceptedAnswer":{"@type":"Answer","text":"Yes. Voice personality, tone (formal vs. casual), escalation thresholds, and response templates are all configurable in the admin console."}}]}]}} />
      {/* Sticky Timeline Sidebar (desktop only) */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-purple-600 border-purple-600 scale-125'
                      : isPast
                      ? 'bg-purple-400 border-purple-400'
                      : 'bg-white border-slate-300 group-hover:border-purple-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-purple-600 font-semibold'
                      : isPast
                      ? 'text-purple-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Opening Section */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-rose-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              AI Customer Care for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600">
                Exceptional Service Experiences
              </span>
            </h1>
            <p className="text-xl text-purple-600 font-medium">
              Deliver personalized, empathetic customer support at scale with AI-powered automation
            </p>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-purple-50 border-l-4 border-purple-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-purple-600">digian.ai AI Customer Care</strong> revolutionizes customer service with intelligent automation that understands context, shows empathy, and delivers exceptional support experiences across all channels.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Headphones className="w-6 h-6 text-purple-600" /> Key Capabilities:
                  </h3>
                  <div className="space-y-3">
                    {capabilities.map((cap, i) => (
                      <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3 p-3 bg-white rounded-xl hover:translate-x-1 transition-transform hover:shadow-md">
                        <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-800">{cap}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-purple-200 mb-2">GOAL</h4>
                    <p className="text-lg">Elevate customer satisfaction while reducing support costs</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-purple-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">Happier customers, efficient operations, scalable support</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Product <span className="text-purple-600">Features</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Advanced AI capabilities for superior customer care</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${f.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mb-3" />
                  <h4 className="text-lg font-bold mb-1">{f.title}</h4>
                  <p className="text-sm text-white/90">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Business <span className="text-purple-600">Benefits</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Transform your customer service operations</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-5 bg-white rounded-xl border-l-4 border-purple-500">
                  <Icon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800 font-medium">{b.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Challenge Section */}
      <section id="challenge" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <AlertTriangle className="w-4 h-4" /> THE CHALLENGE
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Customers Expect Instant Support. <span className="text-purple-600">Teams Need Scale.</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Customers want fast resolution, clear answers, and support on their preferred channel. Support teams face rising ticket volumes, growing costs, and inconsistent experiences across phone, chat, and email.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {painPoints.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="flex items-start gap-4 p-5 bg-orange-50 border-l-4 border-orange-400 rounded-r-xl">
                  <Icon className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800">{p.title}</p>
                    <p className="text-sm text-slate-500 mt-1">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section id="solution" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <Zap className="w-4 h-4" /> THE SOLUTION
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              A Unified AI Agent +{' '}
              <span className="text-purple-600">Workflow Automation Engine</span>
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              digian.ai combines an AI customer care agent with enterprise workflow automation to resolve customer requests end-to-end.
            </p>
          </motion.div>

          {/* Architecture Flow */}
          <motion.div {...fadeUp} className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-10">
            <p className="font-bold text-purple-800 flex items-center gap-2 mb-6">
              <Network className="w-5 h-5" /> How it works
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {[
                { label: 'Customer', sub: 'Voice / Chat / Email', light: true },
                null,
                { label: 'digian.ai AI Agent', sub: 'NLU + policies + identity' },
                null,
                { label: 'Automation Engine', sub: 'Workflows & rules' },
                null,
                { label: 'Enterprise Systems', sub: 'CRM + Ticketing + Billing' },
              ].map((item, i) =>
                item === null ? (
                  <ArrowRight key={i} className="w-6 h-6 text-purple-400 flex-shrink-0" />
                ) : (
                  <div
                    key={i}
                    className={`rounded-xl px-5 py-4 text-center font-semibold shadow-sm ${
                      item.light
                        ? 'bg-white border border-purple-200 text-purple-800'
                        : 'bg-gradient-to-br from-purple-600 to-pink-600 text-white'
                    }`}
                  >
                    <div>{item.label}</div>
                    <div className={`text-xs mt-1 font-normal ${item.light ? 'text-purple-500' : 'text-white/80'}`}>{item.sub}</div>
                  </div>
                )
              )}
            </div>
          </motion.div>

          <motion.div {...fadeUp}>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
              <Gift className="w-6 h-6 text-purple-600" /> What you get
            </h3>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutionCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="bg-white rounded-xl border border-purple-100 p-6 hover:shadow-md hover:-translate-y-1 transition-all">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-purple-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-1">{c.title}</h4>
                  <p className="text-sm text-slate-500">{c.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Business Impact Section */}
      <section id="impact" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <TrendingUp className="w-4 h-4" /> BUSINESS IMPACT
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Improve Service Outcomes While{' '}
              <span className="text-purple-600">Reducing Cost-to-Serve</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              By automating routine inquiries and accelerating complex resolutions, AI customer care improves responsiveness, lowers operational cost, and boosts customer satisfaction.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {impactMetrics.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="bg-white border border-purple-100 rounded-xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all">
                  <Icon className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-purple-700 my-3">{m.value}</div>
                  <div className="font-semibold text-slate-800 mb-2">{m.label}</div>
                  <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">{m.tag}</span>
                </motion.div>
              );
            })}
          </div>

          {/* Quality Metrics */}
          <motion.div {...fadeUp}>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Quality &amp; <span className="text-purple-600">Resolution Improvements</span>
            </h3>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {qualityMetrics.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="bg-white border border-purple-100 rounded-xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all">
                  <Icon className="w-9 h-9 text-purple-500 mx-auto mb-3" />
                  <div className="text-2xl font-bold text-purple-700 my-2">{m.value}</div>
                  <div className="font-semibold text-slate-800 mb-1 text-sm">{m.label}</div>
                  <span className="inline-block bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full mb-2">{m.tag}</span>
                  <p className="text-xs text-slate-500 mt-2">{m.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Key Quality Enablers */}
          <motion.div {...fadeUp} className="bg-gradient-to-r from-purple-700 to-pink-700 rounded-xl p-5 flex flex-wrap items-center gap-6">
            <span className="text-white font-bold text-sm uppercase tracking-widest">Key Quality Enablers</span>
            {[
              { icon: BookOpen, label: 'Grounded Knowledge Base' },
              { icon: Shield, label: 'Policy-Controlled Actions' },
              { icon: ClipboardList, label: 'Full Audit Trail' },
            ].map((e, i) => {
              const Icon = e.icon;
              return (
                <div key={i} className="flex items-center gap-2 text-purple-100">
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{e.label}</span>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Always-On Deflection Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <Clock className="w-4 h-4" /> 24/7 AUTOMATION
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Always-On <span className="text-purple-600">Deflection & Containment</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Resolve Routine Requests Without Agent Intervention</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {deflectionCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="flex items-start gap-4 p-5 bg-white rounded-xl border-l-4 border-purple-500 hover:shadow-md transition-shadow">
                  <Icon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800">{c.title}</p>
                    <p className="text-sm text-slate-500 mt-1">{c.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 flex items-center gap-5">
            <Zap className="w-10 h-10 text-white flex-shrink-0" />
            <div>
              <p className="font-bold text-white text-lg mb-1">Outcome</p>
              <p className="text-purple-100">40–60%+ deflection for routine requests; seamless human handoff for complex cases with full conversation context</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <Package className="w-4 h-4" /> WORKFLOW LIBRARY
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              High-Value Customer Care <span className="text-purple-600">Use Cases</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Deploy quickly with common customer care workflows and expand over time</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((uc, i) => {
              const Icon = uc.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-gradient-to-br from-purple-700 to-pink-700 rounded-xl p-6 text-white shadow-lg">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/30">
                    <Icon className="w-8 h-8 text-purple-200 flex-shrink-0" />
                    <h4 className="font-bold text-sm uppercase tracking-wide">{uc.title}</h4>
                  </div>
                  <ul className="space-y-3">
                    {uc.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-purple-100">
                        <CheckCircle2 className="w-4 h-4 text-purple-300 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <Puzzle className="w-4 h-4" /> ENTERPRISE ECOSYSTEM
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Integrates With Your CRM and{' '}
              <span className="text-purple-600">Customer Care Ecosystem</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Real-time workflows and data-driven support through custom APIs and MCP-based integrations
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {integrations.map((intg, i) => {
              const Icon = intg.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="bg-white border border-purple-100 rounded-xl p-5 hover:border-purple-400 hover:shadow-md hover:-translate-y-1 transition-all">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-purple-600" />
                    <h4 className="font-bold text-slate-800 text-sm">{intg.title}</h4>
                  </div>
                  <p className="text-sm text-slate-500 leading-relaxed">{intg.items}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 flex items-center gap-5">
            <Puzzle className="w-10 h-10 text-white flex-shrink-0" />
            <div>
              <p className="font-bold text-white text-lg mb-1">50+ Integration Options</p>
              <p className="text-purple-100">Available via MCP toolkits + connector ecosystem for seamless enterprise connectivity</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ROI Section */}
      <section id="roi" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <DollarSign className="w-4 h-4" /> BUSINESS VALUE
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Fast Time-to-Value With{' '}
              <span className="text-purple-600">Measurable ROI</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              AI customer care improves efficiency and customer experience at the same time, driving measurable return through automation and reduced handle time
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {roiMetrics.map((m, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl p-8 text-white text-center shadow-lg">
                <div className="text-4xl font-bold mb-2">{m.value}</div>
                <div className="text-purple-100 font-medium">{m.label}</div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp}>
            <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
              <TrendingUp className="w-6 h-6 text-purple-600" /> ROI Drivers
            </h3>
          </motion.div>
          <div className="space-y-4">
            {roiDrivers.map((d, i) => {
              const Icon = d.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="flex items-start gap-4 p-5 bg-purple-50 border-l-4 border-purple-500 rounded-r-xl">
                  <Icon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800">{d.title}</p>
                    <p className="text-sm text-slate-500 mt-1">{d.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              <Map className="w-4 h-4" /> IMPLEMENTATION
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Phased Rollout: <span className="text-purple-600">Start Small, Scale Fast</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Proven implementation methodology with measurable milestones at each phase
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {roadmapPhases.map((phase, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white border border-purple-100 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-5">
                  <h4 className="text-lg font-bold text-white">{phase.title}</h4>
                  <p className="text-purple-200 text-sm mt-1">{phase.duration}</p>
                </div>
                <ul className="p-5 space-y-3">
                  {phase.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-slate-700 border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                      <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'Which channels does AI Customer Care support?', a: 'Voice, web chat, email, and SMS are supported out of the box. Social media channels (WhatsApp, Facebook Messenger) are available on Enterprise plans.' },
              { q: 'How does human handoff work?', a: 'When the AI detects a complex issue or low confidence, it seamlessly transfers the conversation, with full context, to a live agent. Handoff triggers are configurable.' },
              { q: 'What languages are supported?', a: 'The AI Customer Care agent supports multiple languages. Language detection is automatic. Contact us for the full list of supported languages.' },
              { q: 'How long does implementation take?', a: 'Most customers are live within 2–4 weeks. Enterprise deployments with deep integrations may take 6–8 weeks. A dedicated onboarding engineer guides the process.' },
              { q: 'Can I customize the AI persona and tone?', a: 'Yes. Voice personality, tone (formal vs. casual), escalation thresholds, and response templates are all configurable in the admin console.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Ready to Transform Your <span className="text-purple-200">Customer Care?</span>
            </h2>
            <p className="text-xl text-purple-100">
              Deliver exceptional service experiences with AI-powered automation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AICustomerCarePage;
